import { sortBy, uniq } from 'lodash';
import moment from 'moment';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const DAY_INTERVAL = 'days';
const MONTH_INTERVAL = 'months';

const DATE_FORMAT = 'YYYY-MM-DD';
const DATE_MONTH_ONLY_FORMAT = 'YYYY-MM';

const DATE_FORMATS_TO_INTERVALS_MAP = {
  [DAY_INTERVAL]: DATE_FORMAT,
  [MONTH_INTERVAL]: DATE_MONTH_ONLY_FORMAT,
};

export const toLocalHyphenatedDateString = (dateObject: Date) => moment(dateObject).local().format(DATE_FORMAT);

/** Returns the current date (YYYY-MM-DD) as observed in the given IANA timezone, e.g. "America/New_York". */
export const getDateInTimezone = (timezone: string, dateObject: Date = new Date()): string =>
  dayjs(dateObject).tz(timezone).format(DATE_FORMAT);

export const shiftDateString = (date: string, days: number): string => dayjs(date).add(days, DAY_INTERVAL).format(DATE_FORMAT);

export const getNextWorkdayAfter = (numberOfDaysToAdd: number, date: string = toLocalHyphenatedDateString(new Date())) => {
  const dateMoment = moment(date).add(numberOfDaysToAdd, DAY_INTERVAL);
  const shouldExtendBy = dateMoment.isoWeekday() === 6 ? 2 : dateMoment.isoWeekday() === 7 ? 1 : 0;

  return dateMoment.add(shouldExtendBy, DAY_INTERVAL).format(DATE_FORMAT);
};

const trimDate = (date: string, interval?: string) => date.slice(0, interval === MONTH_INTERVAL ? 7 : 10);

export const datesAreAdjacent = (date: string, comparedDate: string, interval: moment.unitOfTime.Diff) => {
  if (!date || !comparedDate) {
    return;
  }
  return Math.abs(moment(trimDate(date, interval)).diff(moment(trimDate(comparedDate, interval)), interval)) === 1;
};

export const getFirstDayOfTheMonth = (monthDate: string) => {
  return `${trimDate(monthDate, MONTH_INTERVAL)}-01`;
};

export const getLastDayOfTheMonth = (monthDate: string) => {
  return moment(getFirstDayOfTheMonth(monthDate)).endOf(MONTH_INTERVAL).format(DATE_FORMAT);
};

export const coversFullMonth = (startDate: string, endDate: string) => {
  const firstCandidateMonth =
    startDate === getFirstDayOfTheMonth(startDate) ? startDate : shiftDateString(getLastDayOfTheMonth(startDate), 1);

  return getLastDayOfTheMonth(firstCandidateMonth) <= endDate;
};

const listDatesFromDateRange = (startDate: string, endDate: string, interval: 'days' | 'months') => {
  const addedDate = moment(trimDate(startDate, interval));
  const endDateObj = moment(trimDate(endDate, interval));

  const dates = [];
  while (addedDate <= endDateObj) {
    dates.push(addedDate.format(DATE_FORMATS_TO_INTERVALS_MAP[interval]));
    addedDate.add(1, interval);
  }
  return dates;
};

export const listMonthsBetweenTwoDates = (startDate: string, endDate: string) => listDatesFromDateRange(startDate, endDate, MONTH_INTERVAL);
export const listDaysBetweenTwoDates = (startDate: string, endDate: string) => listDatesFromDateRange(startDate, endDate, DAY_INTERVAL);

const getDateRangesFromDateList = (dates: string[], interval: moment.unitOfTime.Diff) => {
  const sortedDates = sortBy(uniq(dates));
  return sortedDates.reduce((arr, curr, index) => {
    const nextDate = datesAreAdjacent(curr, sortedDates[index - 1]!, interval);
    if (nextDate) {
      arr[arr.length - 1] = { ...(arr[arr.length - 1] || {}), endDate: trimDate(curr) };
    } else {
      arr.push({ startDate: trimDate(curr, interval), endDate: trimDate(curr, interval) });
    }
    return arr;
  }, [] as { startDate?: string; endDate: string }[]);
};
export const getDayRangesFromDaysList = (days: string[]) => getDateRangesFromDateList(days, DAY_INTERVAL);

export const getDaysWithinRange = (rangePerPath: { startDate?: string; endDate?: string }, days: string[]) => {
  if (!rangePerPath.startDate && !rangePerPath.endDate) {
    return days;
  }

  const sortedDays = sortBy(uniq(days));
  if (sortedDays[0]! > rangePerPath.endDate! || sortedDays[sortedDays.length - 1]! < rangePerPath.startDate!) {
    return [];
  }

  const availabilityDays = listDaysBetweenTwoDates(
    rangePerPath?.startDate ?? sortedDays[0]!,
    rangePerPath?.endDate ?? sortedDays[sortedDays.length - 1]!
  );
  return sortedDays.filter((day) => availabilityDays.includes(day));
};

export const getMonthRangesFromMonthList = (months: string[], customAvailability?: { startDate: string; endDate: string }) => {
  const ranges = getDateRangesFromDateList(months, MONTH_INTERVAL).map(({ startDate, endDate }) => ({
    startDate: getFirstDayOfTheMonth(startDate!),
    endDate: getLastDayOfTheMonth(endDate), // if delayed, get today or yesterday based on the time and the time defined under the feed
  }));

  if (customAvailability) {
    const { startDate: customAvailabilityStartDate, endDate: customAvailabilityEndDate } = customAvailability;
    return ranges
      .map(({ startDate, endDate }) => {
        if (endDate < customAvailabilityStartDate || startDate > customAvailabilityEndDate) {
          return null;
        }
        const adjustedStart = startDate < customAvailabilityStartDate ? customAvailabilityStartDate : startDate;
        const adjustedEnd = endDate > customAvailabilityEndDate ? customAvailabilityEndDate : endDate;

        return { startDate: adjustedStart, endDate: adjustedEnd };
      })
      .filter(Boolean);
  }

  return ranges;
};
